// pfs-modal.ts — generisches Modal/Dialog (PFS-584, Epic PFS-591).
//
// Vier Zonen (Header · Content · Status · Footer). Header = data-title / slot
// "header"; Content = default-Slot; Status = tone-gefaerbte Zeile; Footer =
// vom Modal aus einem actions-Spec IN SEINER SHADOW gerenderte Buttons
// (immer gestylt/brandbar, egal ob das Modal am document.body oder in einer
// Cut-App-Shadow haengt) — plus optional ein footer-Slot fuer frei geslottete
// Buttons (Frame-only-Fall).
//
// Footer-Anordnung: eine danger-Sekundaeraktion wird links abgesetzt, Abbrechen
// + Primaeraktion clustern rechts — ABER nur, wenn es ueberhaupt eine primaere
// (primary/success) Aktion gibt. Ist der danger-Button selbst die Bestaetigung
// (z.B. confirmDialog: nur Abbrechen + danger), bleibt er rechts.
//
// PFS-336-Lektion: Backdrop als Child (.backdrop, inset:0), NICHT :host.
//
// Zwei Nutzungsarten:
//   (a) deklarativ als Rahmen: <pfs-modal data-title data-open> … slot … </>
//   (b) aktions-orientiert: el.setActions([...]) (siehe openModal / DialogResult).
//
// Attribute (alle data-*): data-open · data-size sm|md|lg · data-title ·
//   data-status(+data-status-tone error|warning|success|info) · data-static ·
//   data-no-backdrop-close · data-no-escape-close · data-no-dismiss-button.
// Events (bubbles+composed): pfs:modal-open · pfs:modal-close { reason, value }.
// Methoden am Host: show() · close(reason?) · setActions(actions).
//
// Voll brandbar (nur --pfs-color-*-Tokens), Dark folgt Dokument-:root.
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { escapeHTML } from "../../utils/escape-html.js";

export type PfsModalCloseReason = "backdrop" | "escape" | "dismiss" | "action" | "programmatic";

export type ModalTone = "error" | "warning" | "success" | "info";
export interface ModalStatus {
  text: string;
  tone?: ModalTone;
}

// Rueckgabe-Vertrag jeder Aktion: schliessen (mit Ergebnis) oder offen bleiben.
export type DialogResult<R> =
  | { readonly close: true; readonly value: R }
  | { readonly close: false; readonly status?: ModalStatus };

export const DialogResult = {
  close<R>(value: R): DialogResult<R> {
    return { close: true, value };
  },
  keepOpen(status?: ModalStatus): DialogResult<never> {
    return { close: false, status };
  },
};

export interface ModalAction<R> {
  label: string;
  variant?: "primary" | "danger" | "ghost" | "success";
  onClick?: () => DialogResult<R> | Promise<DialogResult<R>>;
  enabled?: boolean | (() => boolean); // reaktiv -> false = disabled
  visible?: boolean | (() => boolean); // reaktiv -> false = ausgeblendet
  busyLabel?: string;
}

export interface PfsModalElement extends HTMLElement {
  show(): void;
  close(reason?: PfsModalCloseReason): void;
  setActions(actions: ModalAction<unknown>[]): void;
}

interface ModalHost extends HTMLElement {
  show?: () => void;
  close?: (reason?: PfsModalCloseReason) => void;
  setActions?: (actions: ModalAction<unknown>[]) => void;
  _pfsPendingReason?: PfsModalCloseReason;
  _pfsPendingValue?: unknown;
}

const STYLES = `
${SHADOW_RESET_CSS}
:host { position: fixed; inset: 0; z-index: 1100; display: none; }
:host([data-open]) { display: block; }
.backdrop {
  position: absolute; inset: 0;
  background: var(--pfs-overlay-scrim-bg, rgba(15, 23, 42, 0.45));
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
}
.dialog {
  background: var(--pfs-color-surface);
  color: var(--pfs-color-on-surface);
  border: 1px solid var(--pfs-color-outline);
  border-radius: var(--pfs-radius-md, 0.5rem);
  box-shadow: var(--pfs-shadow-card, 0 10px 30px rgba(0, 0, 0, 0.25));
  width: 100%; max-width: var(--pfs-modal-width, 512px);
  max-height: calc(100vh - 2rem);
  display: flex; flex-direction: column; overflow: hidden;
}
:host([data-size="sm"]) .dialog { max-width: var(--pfs-modal-width, 420px); }
:host([data-size="lg"]) .dialog { max-width: var(--pfs-modal-width, 640px); }
.head { display: flex; align-items: flex-start; gap: 1rem; padding: 1.25rem 1.5rem; border-bottom: 1px solid var(--pfs-color-outline); }
.head.is-empty { display: none; }
.title { margin: 0; font-size: 1.05rem; font-weight: 600; color: var(--pfs-color-on-surface); min-width: 0; flex: 1; }
.title:empty { display: none; }
.dismiss {
  margin-left: auto; flex-shrink: 0; font: inherit; cursor: pointer;
  background: none; border: none; line-height: 1; font-size: 1.5rem;
  padding: 0.1rem 0.4rem; border-radius: var(--pfs-radius-sm, 0.375rem);
  color: var(--pfs-color-on-surface-muted);
}
.dismiss:hover { background: var(--pfs-color-surface-hover); color: var(--pfs-color-on-surface); }
.dismiss:focus-visible { outline: 2px solid var(--pfs-color-primary); outline-offset: 2px; }
:host([data-static]) .dismiss, :host([data-no-dismiss-button]) .dismiss { display: none; }
.body { padding: 1rem 1.5rem; overflow: auto; }
.status { padding: 0.7rem 1.5rem; font-size: 0.85rem; line-height: 1.4; background: var(--pfs-color-surface-sunken); color: var(--pfs-color-on-surface-muted); }
.status:empty { display: none; }
:host([data-status-tone="error"])   .status { background: var(--pfs-color-danger-container);   color: var(--pfs-color-on-danger-container); }
:host([data-status-tone="warning"]) .status { background: var(--pfs-color-warning-container); color: var(--pfs-color-on-warning-container); }
:host([data-status-tone="success"]) .status { background: var(--pfs-color-success-container); color: var(--pfs-color-on-success-container); }
:host([data-status-tone="info"])    .status { background: var(--pfs-color-info-container);    color: var(--pfs-color-on-info-container); }
.foot { display: flex; align-items: center; gap: 0.5rem; padding: 1rem 1.5rem 1.5rem; flex-wrap: wrap; border-top: 1px solid var(--pfs-color-outline); }
.foot.is-empty { display: none; }
/* Footer-Cluster: danger-Sekundäraktionen links abgesetzt, Abbrechen+Primär
   rechts (nur wenn eine Primäraktion existiert — sonst ist der danger-Button
   selbst die Bestätigung und bleibt rechts). margin-left:auto schiebt die
   rechte Gruppe nach rechts, auch wenn die linke leer ist. */
.acts-left { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.acts-right { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-left: auto; }
.act { font: inherit; cursor: pointer; font-weight: 500; border-radius: var(--pfs-radius-sm, 0.375rem); padding: 0.5rem 1rem; border: 1px solid transparent; display: inline-flex; align-items: center; justify-content: center; gap: 0.45rem; }
.act:focus-visible { outline: 2px solid var(--pfs-color-primary); outline-offset: 2px; }
.act[disabled] { opacity: 0.55; cursor: not-allowed; }
.act.ghost { background: var(--pfs-color-surface); border-color: var(--pfs-color-outline); color: var(--pfs-color-on-surface); }
.act.ghost:hover:not([disabled]) { background: var(--pfs-color-surface-hover); }
.act.primary { background: var(--pfs-color-primary); color: var(--pfs-color-on-primary); }
.act.danger { background: var(--pfs-color-danger); color: var(--pfs-color-on-primary); }
.act.success { background: var(--pfs-color-success); color: var(--pfs-color-on-primary); }
.act-spin { width: 0.9em; height: 0.9em; border: 2px solid currentColor; border-right-color: transparent; border-radius: 50%; animation: pfs-modal-spin 0.6s linear infinite; }
@keyframes pfs-modal-spin { to { transform: rotate(360deg); } }
@media (max-width: 33rem) {
  .foot { flex-direction: column-reverse; align-items: stretch; }
  .acts-left, .acts-right { flex-direction: column-reverse; margin-left: 0; width: 100%; }
  .foot ::slotted(*), .foot .act { width: 100%; }
}
`;

defineComponent({
  tagName: "pfs-modal",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const h = host as ModalHost;

    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const backdrop = document.createElement("div");
    backdrop.className = "backdrop";
    backdrop.setAttribute("part", "backdrop");
    backdrop.innerHTML = `
      <div class="dialog" role="dialog" aria-modal="true" tabindex="-1" part="dialog">
        <div class="head" part="header">
          <slot name="header"><h2 class="title"></h2></slot>
          <button type="button" class="dismiss" aria-label="Schließen">&times;</button>
        </div>
        <div class="body" part="body"><slot></slot></div>
        <div class="status" part="status" role="status" aria-live="polite"></div>
        <div class="foot" part="footer"><div class="acts-left"></div><div class="acts-right"></div><slot name="footer"></slot></div>
      </div>`;
    root.appendChild(backdrop);

    const dialog = backdrop.querySelector(".dialog") as HTMLElement;
    const head = backdrop.querySelector(".head") as HTMLElement;
    const titleEl = backdrop.querySelector(".title") as HTMLElement;
    const statusEl = backdrop.querySelector(".status") as HTMLElement;
    const headerSlot = backdrop.querySelector('slot[name="header"]') as HTMLSlotElement;
    const footSlot = backdrop.querySelector('slot[name="footer"]') as HTMLSlotElement;
    const foot = backdrop.querySelector(".foot") as HTMLElement;
    const actsLeft = backdrop.querySelector(".acts-left") as HTMLElement;
    const actsRight = backdrop.querySelector(".acts-right") as HTMLElement;

    const paint = (): void => {
      const t = host.dataset.title ?? "";
      titleEl.textContent = t;
      if (t) dialog.setAttribute("aria-label", t);
      else dialog.removeAttribute("aria-label");
      statusEl.textContent = host.dataset.status ?? "";
    };
    const paintHead = (): void => {
      const hasTitle = (host.dataset.title ?? "").trim() !== "";
      const hasHeaderSlot = headerSlot.assignedNodes().length > 0;
      const dismissHidden =
        host.hasAttribute("data-static") || host.hasAttribute("data-no-dismiss-button");
      head.classList.toggle("is-empty", !hasTitle && !hasHeaderSlot && dismissHidden);
    };
    const paintFoot = (): void => {
      const empty =
        actsLeft.childElementCount === 0 &&
        actsRight.childElementCount === 0 &&
        footSlot.assignedNodes().length === 0;
      foot.classList.toggle("is-empty", empty);
    };
    paint();
    paintHead();
    paintFoot();

    // ── Open/Close-Lebenszyklus (data-open = einzige Wahrheit) ──
    let isOpen = false;
    let prevFocus: Element | null = null;

    const focusInitial = (): void => {
      const auto = host.querySelector<HTMLElement>("[autofocus]");
      const first = host.querySelector<HTMLElement>(
        'button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      );
      (auto ?? first ?? dialog).focus();
    };

    const doOpen = (): void => {
      if (isOpen) return;
      isOpen = true;
      prevFocus = (host.getRootNode() as Document | ShadowRoot).activeElement ?? document.activeElement;
      focusInitial();
      host.dispatchEvent(new CustomEvent("pfs:modal-open", { bubbles: true, composed: true }));
    };
    const doClose = (): void => {
      if (!isOpen) return;
      isOpen = false;
      const reason = h._pfsPendingReason ?? "programmatic";
      const value = h._pfsPendingValue;
      h._pfsPendingReason = undefined;
      h._pfsPendingValue = undefined;
      host.dispatchEvent(
        new CustomEvent<{ reason: PfsModalCloseReason; value: unknown }>("pfs:modal-close", {
          bubbles: true,
          composed: true,
          detail: { reason, value },
        }),
      );
      if (prevFocus instanceof HTMLElement) prevFocus.focus();
    };

    const requestClose = (reason: PfsModalCloseReason, value?: unknown): void => {
      h._pfsPendingReason = reason;
      h._pfsPendingValue = value;
      host.removeAttribute("data-open"); // → Observer ruft doClose
    };

    h.show = (): void => host.setAttribute("data-open", "");
    h.close = (reason: PfsModalCloseReason = "programmatic"): void => requestClose(reason);

    // ── Aktionen (in der Shadow gerendert) ──
    const setStatus = (s?: ModalStatus): void => {
      if (s?.text) {
        host.setAttribute("data-status", s.text);
        host.setAttribute("data-status-tone", s.tone ?? "info");
      } else {
        host.removeAttribute("data-status");
        host.removeAttribute("data-status-tone");
      }
      paint();
    };
    const evalPred = (p: boolean | (() => boolean) | undefined, dflt: boolean): boolean =>
      p === undefined ? dflt : typeof p === "function" ? p() : p;

    let actionEntries: { action: ModalAction<unknown>; btn: HTMLButtonElement }[] = [];
    const refreshActions = (): void => {
      for (const { action, btn } of actionEntries) {
        btn.hidden = !evalPred(action.visible, true);
        if (!btn.dataset.busy) btn.disabled = !evalPred(action.enabled, true);
      }
    };
    const runAction = async (action: ModalAction<unknown>, btn: HTMLButtonElement): Promise<void> => {
      if (!isOpen || btn.disabled) return;
      setStatus(undefined);
      if (!action.onClick) {
        requestClose("action", undefined);
        return;
      }
      btn.dataset.busy = "1";
      actionEntries.forEach((e) => (e.btn.disabled = true));
      btn.innerHTML = `<span class="act-spin" aria-hidden="true"></span>${escapeHTML(action.busyLabel ?? action.label)}`;
      try {
        const res = await action.onClick();
        if (res.close) {
          requestClose("action", res.value);
          return;
        }
        setStatus(res.status);
      } catch (err) {
        setStatus({ text: "Aktion fehlgeschlagen", tone: "error" });
        console.error("[pfs-modal] action error", err);
      } finally {
        delete btn.dataset.busy;
        btn.textContent = action.label;
        refreshActions();
      }
    };
    h.setActions = (actions: ModalAction<unknown>[]): void => {
      actsLeft.textContent = "";
      actsRight.textContent = "";
      // danger-Sekundäraktion nur links absetzen, wenn es AUCH eine primäre
      // (primary/success) Aktion gibt — sonst ist der danger-Button selbst die
      // Bestätigung (z.B. confirmDialog) und gehört nach rechts.
      const hasPrimary = actions.some((a) => a.variant === "primary" || a.variant === "success");
      actionEntries = actions.map((action) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = `act ${action.variant ?? "ghost"}`;
        btn.textContent = action.label;
        btn.addEventListener("click", () => void runAction(action, btn));
        (hasPrimary && action.variant === "danger" ? actsLeft : actsRight).appendChild(btn);
        return { action, btn };
      });
      refreshActions();
      paintFoot();
    };
    // reaktive Praedikate: bei jeder Eingabe im Content neu auswerten
    host.addEventListener("input", refreshActions);
    host.addEventListener("change", refreshActions);

    // Backdrop-Klick (nur auf den Backdrop selbst, nicht auf Dialog-Kinder)
    backdrop.addEventListener("click", (e) => {
      if (e.target !== backdrop) return;
      if (host.hasAttribute("data-static") || host.hasAttribute("data-no-backdrop-close")) return;
      requestClose("backdrop");
    });
    // ×-Button + beliebiges [data-close] im Light-DOM
    backdrop.querySelector(".dismiss")?.addEventListener("click", () => requestClose("dismiss"));
    host.addEventListener("click", (e) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest("[data-close]")) requestClose("dismiss");
    });
    // Escape (document-weit; nur wenn offen + erlaubt)
    const onKey = (e: KeyboardEvent): void => {
      if (!isOpen || e.key !== "Escape") return;
      if (host.hasAttribute("data-static") || host.hasAttribute("data-no-escape-close")) return;
      requestClose("escape");
    };
    document.addEventListener("keydown", onKey);
    ctx.cleanup.push(() => document.removeEventListener("keydown", onKey));
    // Focus-Trap über die Shadow-Grenze: fällt der Fokus aus dem Modal, zurückholen
    const onFocusIn = (e: FocusEvent): void => {
      if (!isOpen) return;
      const t = e.target as Node | null;
      if (t && t !== host && !host.contains(t)) dialog.focus();
    };
    document.addEventListener("focusin", onFocusIn);
    ctx.cleanup.push(() => document.removeEventListener("focusin", onFocusIn));

    // data-open / data-title / Slot-Reaktivität
    const sync = (): void => (host.hasAttribute("data-open") ? doOpen() : doClose());
    const obs = new MutationObserver(() => {
      paint();
      paintHead();
      sync();
    });
    obs.observe(host, {
      attributes: true,
      attributeFilter: ["data-open", "data-title", "data-status", "data-static", "data-no-dismiss-button"],
    });
    headerSlot.addEventListener("slotchange", paintHead);
    footSlot.addEventListener("slotchange", paintFoot);
    ctx.cleanup.push(() => obs.disconnect());

    // Initialzustand (falls schon mit data-open gemountet, z.B. imperativ)
    sync();
  },
});
